/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { ComponentProps } from "react";
import { useState } from "react";
import {
  Line,
  LineChart as LineChartRecharts,
  ResponsiveContainer,
  Tooltip as TooltipRecharts,
  XAxis,
  YAxis,
} from "recharts";

import { spacing } from "../../../../../../global/emotion";
import { useThemeValues } from "../../../../../hooks/useThemeValues";
import { Skeleton } from "../../../_core/Skeleton/Skeleton.emotion";
import { Dot } from "../Dot/Dot.emotion";
import { Tick } from "../Tick/Tick.emotion";
import { Tooltip } from "../Tooltip/Tooltip.emotion";

/**
 * Unfortunatley, lazy loading of the recharts components is currently not available
 * {@see https://github.com/recharts/recharts/issues/2566}
 */
// const Line = lazy(() => import("recharts").then(module => ({ default: module.Line })));
// const LineChartRecharts = lazy(() => import("recharts").then(module => ({ default: module.LineChart })));
// const ResponsiveContainer = lazy(() => import("recharts").then(module => ({ default: module.ResponsiveContainer })));
// const TooltipRecharts = lazy(() => import("recharts").then(module => ({ default: module.Tooltip })));
// const XAxis = lazy(() => import("recharts").then(module => ({ default: module.XAxis })));
// const YAxis = lazy(() => import("recharts").then(module => ({ default: module.YAxis })));

const containerStyle: Interpolation<{ height: number }> = (props) => ({
  height: props.height,
  width: spacing.full,
});

const loadingContainerStyle: Interpolation<{ height: number }> = (props) => ({
  height: props.height,
  width: spacing.full,
});

export type LineChartProps = ComponentProps<typeof LineChartRecharts> & {
  height?: number;
  loading?: boolean;
  xAxisInterval?: number;
};

export const LineChart = ({
  data,
  height = 260,
  loading,
  xAxisInterval = 0,
  ...props
}: LineChartProps) => {
  const [activeDotCy, setActiveDotCy] = useState<number>();

  const theme = useThemeValues();

  if (loading) return <Skeleton css={loadingContainerStyle({ height })} />;

  return (
    <div css={containerStyle({ height })}>
      <ResponsiveContainer>
        <LineChartRecharts
          data={data}
          onMouseMove={(state) => {
            if (
              state.isTooltipActive &&
              state.activeCoordinate?.y !== undefined
            ) {
              setActiveDotCy(state.activeCoordinate.y);
            }
          }}
          onMouseLeave={() => setActiveDotCy(undefined)}
          {...props}
        >
          <XAxis
            dataKey="tick"
            interval={xAxisInterval}
            stroke={theme?.semanticColors?.neutralsLowest}
            strokeWidth={2}
            tick={<Tick dataLength={data?.length} />}
            tickLine={false}
          />
          <YAxis
            hide
            dataKey="value"
            domain={[
              (dataMin: number) => dataMin * 0.9,
              (dataMax: number) => (dataMax === 0 ? 1 : dataMax * 1.1),
            ]}
          />
          <Line
            activeDot={false}
            dataKey="value"
            dot={false}
            stroke={theme?.semanticColors?.accentMedium}
            strokeWidth={2}
            type="linear"
          />
          <TooltipRecharts
            content={({ payload }) => (
              <Tooltip
                label={payload[0]?.payload?.label}
                value={payload[0]?.value as string}
              />
            )}
            cursor={{
              stroke: theme?.semanticColors?.neutralsLowest,
              strokeWidth: 2,
            }}
            /** Height of Tooltip is ~54px  */
            /** Determine if space exists to center vertically with the activeDot's cy cooridinate */
            position={{
              y:
                activeDotCy > height - 55
                  ? activeDotCy - 54
                  : activeDotCy < 35
                    ? activeDotCy
                    : activeDotCy - 27,
            }}
          />
          <Line
            activeDot={({ cx, cy }) => <Dot x={cx} y={cy} />}
            dataKey="value"
            dot={false}
            stroke="none"
          />
        </LineChartRecharts>
      </ResponsiveContainer>
    </div>
  );
};

LineChart.displayName = "LineChart";
